import React, { useState } from 'react';
import { toast } from 'sonner';
import {
  Calculator as CalcIcon, Clock as ClockIcon, ArrowsLeftRight, ShieldCheck, Warning, Backspace,
} from '@phosphor-icons/react';
import { Button } from './ui/button';
import {
  decoyMode, decoyKey, setDecoy, clearDecoy, applyDisguiseChrome, DISGUISES,
} from '../lib/decoy';

/**
 * Choosing what the phone says this app is.
 *
 * Each disguise has its own kind of door, and the editor has to match it,
 * because the key is compared as a STRING and both sides must produce the same
 * one. The calculator records what was pressed in its own notation -- "7×8",
 * not "7*8" -- so the key is entered on the same keypad it will be entered on
 * later. The clock's key is a time, set on a clock face. The converter's is a
 * number, set on a digit pad.
 *
 * None of the three is a text field, and that is the reason: a typed "07:5" or
 * "7x8" looks right and opens nothing, and the member finds that out only when
 * they need it to work.
 */

const PAD = ['7', '8', '9', '÷', '4', '5', '6', '×', '1', '2', '3', '−', '0', ',', '+'];

// One key is a slip of the thumb. A calculation is a thing you meant to type.
const MIN_LENGTH = 3;
const hasOperator = (s) => /[+\-−×÷]/.test(s);

const two = (n) => String(n).padStart(2, '0');

// Three digits at least. One or two is a number somebody might genuinely
// convert -- 25 cm is a real measurement -- and the door would open on an
// ordinary use of the app.
const MIN_DIGITS = 3;
const DIGITS = ['7', '8', '9', '4', '5', '6', '1', '2', '3', '0'];

const DisguiseSetup = () => {
  const [mode, setMode] = useState(() => decoyMode());
  const [draft, setDraft] = useState('');
  const [time, setTime] = useState({ h: 3, m: 47 });
  // Which disguise is being set up, or null. Not a boolean: the editor below
  // differs per disguise, so it needs to know which one it is editing.
  const [editing, setEditing] = useState(null);

  const current = decoyKey();

  const stop = () => { setEditing(null); setDraft(''); };

  const choosePlain = () => {
    clearDecoy();
    applyDisguiseChrome(null);
    setMode(null);
    stop();
    toast.success('This app looks like Arcanum again');
  };

  const commit = (which, key) => {
    setDecoy(which, key);
    applyDisguiseChrome(which);
    setMode(which);
    stop();
    toast.success('Disguise set. Add the app to your home screen now.');
  };

  const saveCalculator = () => {
    if (draft.length < MIN_LENGTH || !hasOperator(draft)) {
      toast.error('Use a short calculation — a couple of numbers and a sign between them.');
      return;
    }
    commit('calculator', draft);
  };

  const saveClock = () => commit('clock', `${two(time.h)}:${two(time.m)}`);

  const saveConverter = () => {
    if (draft.length < MIN_DIGITS) {
      toast.error('Use at least three digits — a short number is one somebody might really convert.');
      return;
    }
    commit('converter', draft);
  };

  const chooser = (which, icon, blurb, inUse) => (
    <button
      type="button"
      onClick={() => { setEditing(which); setDraft(''); }}
      className={`w-full text-left rounded-lg border p-3 transition-colors ${
        mode === which ? 'border-border bg-muted/40' : 'border-border hover:bg-muted/30'
      }`}
      data-testid={`disguise-${which}`}
    >
      <span className="block text-sm font-medium text-foreground">
        {icon}
        {DISGUISES[which].label}
        {mode === which && (
          <ShieldCheck className="w-4 h-4 inline ml-2" style={{ color: 'hsl(var(--gold))' }} weight="fill" />
        )}
      </span>
      <span className="block text-xs text-muted-foreground">
        {mode === which ? inUse : blurb}
      </span>
    </button>
  );

  return (
    <div className="border border-border rounded-xl bg-card p-5 space-y-4" data-testid="disguise-setup">
      <div className="flex items-center gap-2">
        <CalcIcon className="w-5 h-5" style={{ color: 'hsl(var(--gold))' }} weight="fill" />
        <h2 className="font-heading text-lg">What this app looks like</h2>
      </div>

      <p className="text-sm text-muted-foreground">
        Arcanum can sit on your home screen as something nobody has any reason
        to open. Tapping it opens an app that really works, and something only
        you know opens Arcanum.
      </p>

      {/* The constraint that shapes everything else, said before the choice
          rather than after it. */}
      <div className="flex items-start gap-2 text-xs text-muted-foreground bg-muted/40 border border-border rounded-lg p-3">
        <Warning className="w-4 h-4 mt-0.5 shrink-0" style={{ color: 'hsl(var(--gold))' }} weight="fill" />
        <span>
          The icon and name are copied the moment you add Arcanum to your home
          screen, and there is no way to change them afterwards. So the order
          matters more than anything else on this screen:
          <strong className="text-foreground"> if Arcanum is already on your
          home screen, remove it first</strong>, then choose a disguise here,
          then add it again. Adding it a second time without removing the first
          leaves you looking at the old icon and wondering what went wrong.
          Nothing is lost by removing it: your account, messages and vault live
          on the Society's server, not in the installed app.
        </span>
      </div>

      <div className="space-y-2">
        <button
          type="button"
          onClick={choosePlain}
          className={`w-full text-left rounded-lg border p-3 transition-colors ${
            mode === null ? 'border-border bg-muted/40' : 'border-border hover:bg-muted/30'
          }`}
          data-testid="disguise-none"
        >
          <span className="block text-sm font-medium text-foreground">Arcanum</span>
          <span className="block text-xs text-muted-foreground">
            The real icon and name. No disguise.
          </span>
        </button>

        {chooser(
          'calculator',
          <CalcIcon className="w-4 h-4 inline mr-2 align-[-2px]" />,
          'A calculator that really calculates.',
          `In use. Your calculation is ${current || '—'}. Tap to change it.`,
        )}

        {chooser(
          'clock',
          <ClockIcon className="w-4 h-4 inline mr-2 align-[-2px]" />,
          'A clock that keeps time, with alarms that really ring.',
          `In use. Your alarm is ${current || '—'}. Tap to change it.`,
        )}

        {chooser(
          'converter',
          <ArrowsLeftRight className="w-4 h-4 inline mr-2 align-[-2px]" />,
          'Length, weight, and a wire gauge table.',
          `In use. Your number is ${current || '—'}. Tap to change it.`,
        )}
      </div>

      {editing === 'calculator' && (
        <div className="space-y-3 border-t border-border pt-4" data-testid="disguise-keypad">
          <p className="text-sm text-foreground">Choose your calculation</p>
          <p className="text-xs text-muted-foreground">
            Something you will remember and would never type by accident. Enter
            it here exactly as you will enter it on the calculator.
          </p>

          <div
            className="rounded-lg border border-border bg-muted/40 px-4 py-3 text-2xl tabular-nums text-center"
            style={{ overflowWrap: 'anywhere', minHeight: 56 }}
            data-testid="disguise-draft"
          >
            {draft || <span className="text-muted-foreground text-base">…</span>}
          </div>

          <div className="grid grid-cols-4 gap-2">
            {PAD.map((k) => (
              <Button
                key={k}
                type="button"
                variant="outline"
                onClick={() => setDraft((d) => (d.length < 16 ? d + k : d))}
                className="h-12 text-lg"
                data-testid={`disguise-pad-${k}`}
              >
                {k}
              </Button>
            ))}
            <Button
              type="button"
              variant="outline"
              onClick={() => setDraft((d) => d.slice(0, -1))}
              className="h-12"
              aria-label="Delete the last character"
            >
              <Backspace className="w-5 h-5" />
            </Button>
          </div>

          <div className="flex gap-2">
            <Button
              type="button"
              onClick={saveCalculator}
              disabled={draft.length < MIN_LENGTH || !hasOperator(draft)}
              className="flex-1 bg-primary hover:bg-primary/90"
              data-testid="disguise-save"
            >
              Use this
            </Button>
            <Button type="button" variant="ghost" onClick={stop}>Cancel</Button>
          </div>

          <p className="text-xs text-muted-foreground leading-relaxed">
            Forget it and there is no way back in through the calculator — by
            design, since a way around it would be a way in for anyone else.
            Remove the app from your home screen and add it again to start over.
          </p>
        </div>
      )}

      {editing === 'clock' && (
        <div className="space-y-3 border-t border-border pt-4" data-testid="disguise-clockpad">
          <p className="text-sm text-foreground">Choose your alarm</p>
          <p className="text-xs text-muted-foreground">
            Setting an alarm for this time opens Arcanum. Any other time simply
            becomes an alarm, listed like any other — so a wrong attempt leaves
            something ordinary behind rather than nothing at all.
          </p>
          <p className="text-xs text-muted-foreground">
            Pick a minute you would never set by accident. An odd one, at an
            hour you are asleep.
          </p>

          <div className="flex items-center justify-center gap-3 py-1" data-testid="disguise-clock-draft">
            {[['h', 23, 'Hour'], ['m', 59, 'Minute']].map(([unit, max, label], i) => (
              <React.Fragment key={unit}>
                {i === 1 && <span className="text-3xl text-muted-foreground pb-1">:</span>}
                <div className="flex flex-col items-center gap-1">
                  <Button
                    type="button"
                    variant="outline"
                    className="h-9 w-16"
                    aria-label={`${label} up`}
                    onClick={() => setTime((t) => ({ ...t, [unit]: (t[unit] + 1) % (max + 1) }))}
                    data-testid={`disguise-clock-${unit}-up`}
                  >
                    ▲
                  </Button>
                  <span className="text-4xl font-light tabular-nums w-16 text-center">
                    {two(time[unit])}
                  </span>
                  <Button
                    type="button"
                    variant="outline"
                    className="h-9 w-16"
                    aria-label={`${label} down`}
                    onClick={() => setTime((t) => ({ ...t, [unit]: (t[unit] + max) % (max + 1) }))}
                  >
                    ▼
                  </Button>
                </div>
              </React.Fragment>
            ))}
          </div>

          <div className="flex gap-2">
            <Button
              type="button"
              onClick={saveClock}
              className="flex-1 bg-primary hover:bg-primary/90"
              data-testid="disguise-clock-save"
            >
              Use this
            </Button>
            <Button type="button" variant="ghost" onClick={stop}>Cancel</Button>
          </div>

          <p className="text-xs text-muted-foreground leading-relaxed">
            Forget it and there is no way back in through the clock — by design,
            since a way around it would be a way in for anyone else. Remove the
            app from your home screen and add it again to start over.
          </p>
        </div>
      )}

      {editing === 'converter' && (
        <div className="space-y-3 border-t border-border pt-4" data-testid="disguise-numpad">
          <p className="text-sm text-foreground">Choose your number</p>
          <p className="text-xs text-muted-foreground">
            Typing this into the centimetre field opens Arcanum. Any other
            number is simply converted, which is what the app is for — so a
            wrong attempt shows a conversion rather than nothing.
          </p>
          <p className="text-xs text-muted-foreground">
            Pick something you would never convert. It opens the moment the
            number is complete, so avoid one that is the start of a number you
            might really type.
          </p>

          <div
            className="rounded-lg border border-border bg-muted/40 px-4 py-3 text-2xl tabular-nums text-center"
            style={{ overflowWrap: 'anywhere', minHeight: 56 }}
            data-testid="disguise-number-draft"
          >
            {draft || <span className="text-muted-foreground text-base">…</span>}
          </div>

          <div className="grid grid-cols-3 gap-2">
            {DIGITS.map((k) => (
              <Button
                key={k}
                type="button"
                variant="outline"
                onClick={() => setDraft((d) => (d.length < 9 ? d + k : d))}
                className="h-12 text-lg"
                data-testid={`disguise-digit-${k}`}
              >
                {k}
              </Button>
            ))}
            <Button
              type="button"
              variant="outline"
              onClick={() => setDraft((d) => d.slice(0, -1))}
              className="h-12"
              aria-label="Delete the last digit"
            >
              <Backspace className="w-5 h-5" />
            </Button>
          </div>

          <div className="flex gap-2">
            <Button
              type="button"
              onClick={saveConverter}
              disabled={draft.length < MIN_DIGITS}
              className="flex-1 bg-primary hover:bg-primary/90"
              data-testid="disguise-number-save"
            >
              Use this
            </Button>
            <Button type="button" variant="ghost" onClick={stop}>Cancel</Button>
          </div>

          <p className="text-xs text-muted-foreground leading-relaxed">
            Forget it and there is no way back in through the converter — by
            design, since a way around it would be a way in for anyone else.
            Remove the app from your home screen and add it again to start over.
          </p>
        </div>
      )}

      {/* The honest limit. A disguise that overpromises makes a member less
          careful than they were before, which leaves them worse off than
          having none. */}
      <p className="text-xs leading-relaxed pt-1" style={{ color: 'hsl(var(--gold))' }}>
        This survives a glance at your home screen and a curious tap. It does
        not survive someone who already knows Arcanum exists and is looking for
        it, and it does not survive being watched while you open it.
      </p>
    </div>
  );
};

export default DisguiseSetup;
